import { DateRange } from "@mui/lab";
import { ChipProps } from "@mui/material";
import dayjs from "dayjs";
import { MRT_TableInstance } from "material-react-table";
import { Dispatch, SetStateAction } from "react";
import { InternalUser, InternalUserRole } from "../types/app";
import { Document } from "../reports/dashboard/policyIssues/types";

export interface FundingPackagesData {
    setDateRange: Dispatch<SetStateAction<DateRange<dayjs.Dayjs>>>;
    loading: boolean;
    fundingPackages: FundingPackage[];
    dateRange: DateRange<dayjs.Dayjs>;
    setFundingPackages: Dispatch<SetStateAction<FundingPackage[]>>;
    assignedToMe: boolean;
    setAssignedToMe: Dispatch<SetStateAction<boolean>>;
    fetchFundingPackages: (params?: fetchPackagesParams) => Promise<void>;
    tableRef: React.MutableRefObject<MRT_TableInstance<FundingPackage> | null>;
    setIsDefaultFilter: Dispatch<SetStateAction<boolean>>;
    isDefaultFilter: boolean;
    getAndSetAssignedToUser: () => void;
    assignedToUser: InternalUser | null;
    saveUploadToMomentum: (document: Document, file: File) => Promise<number>;
    newRows: Set<number>;
    fedReferences: Record<number, FedReference[]>;
    madexOnly: boolean;
    setMadexOnly: Dispatch<SetStateAction<boolean>>;
  }

  // Madex orders are Madison 1031 exchange orders (order number prefixed "MADEX")
  export const isMadexOrder = (orderNumber: string | undefined) => !!orderNumber?.toLowerCase().startsWith("madex");

  export const isMadexDepartmentUser = (user: { roles?: InternalUserRole[] } | null | undefined) =>
    user?.roles?.find((role) => role.isPrimaryRole)?.internalUserDepartment?.name === "Madex";

  // Keyed by funding package id; an empty list means funded but no wire released yet
  export interface FedReference {
    reference: string;
    amount: number | null;
    bank: string | null;
    account: string | null;
    sentAt: Date;
  }

  interface fetchPackagesParams {
    useLoader?: boolean | undefined;
    resetPagination?: boolean | undefined;
  }

export interface FundingPackage {
    id: number;
    orderNumber: string;
    requestedDate: Date;
    fundedDate: Date;
    cancelledDate: Date;
    deficientDate: Date;
    packageNumber: number;
    priority: FundingPriority;
    status: FundingPackageStatus;
    createdByEmail: string;
    createdByUser: any;
    fundingAuthorizations: FundingAuthorization[];
    sourcesAndUsesPdfs: SourcesAndUsesPdf[];
    activeLock?: FundingPackageLock | null;

}
export interface FundingPackageLock {
    id: number;
    fundingPackageId: number;
    lockedByEmail: string;
    lockedByName?: string;
    mode: FundingPackageLockMode;
    lockedAt: Date;
    lastHeartbeatAt: Date;
    takeoverRequestedByEmail?: string | null;
    takeoverRequestedByName?: string | null;
    takeoverRequestedAt?: Date | null;
}
export enum FundingPackageLockMode
{
    Edit = 1,
    Review = 2
}
export interface FundingAuthorization {
    id: number;
    momentumDocumentId: number;
    fundingPackageId: number;
    type: AuthorizationType;
    authorizationStatus: AuthorizationStatus;
    contactName: string;
    contactTypeNumber: number;
    momentumDocumentName?: string;
    isAuthorizationRequired: boolean;
    internalUserId: number;
    contacts: FundingAuthorizationContact[];
}
export interface SourcesAndUsesPdf {
    id:number;
    fundingPackageId: number;
    momentumDocumentId: number;
    documentNumber: number;
    momentumDocumentName?: string;
    authorizationStatus: AuthorizationStatus;
}
export interface FundingPackageNote {
  id: number;
  fundingPackageId: number;
  noteText: string;
  updatedAt: Date;
  createdAt: Date;
  createdByUserId: number;
  createdByUser: InternalUser;
}
export interface FundingAuthorizationContact
{
    id: number;
    fundingAuthorizationId: number;
    contactName: string;
    contactTypeNumber: number;
    contactType: string;
}
export enum AuthorizationStatus
{
    Unreviewed = 1,
    Approved = 2,
    Rejected = 3,
}

export enum AuthorizationType
{
    BuyerBorrower = 1,
    Seller = 2,
    Lender = 3,
    Escrow = 4
}

export enum FundingPackageStatus
{
    Draft = 1,
    Requested = 2,
    Deficient = 3,
    Funded = 4,
    Cancelled = 5
}

export enum FundingPriority
{
    PayoffCutoff = 1,
    Now = 2,
    Soon = 3,
    Today = 4,
}
export interface Contact {
    authPdf: File;
}
export const FundingPackageStatusConfig: Record<
FundingPackageStatus,
{
  label: string;
  color: ChipProps["color"];
}
> = {
[FundingPackageStatus.Draft]: {
  label: "Draft",
  color: "default"
},
[FundingPackageStatus.Requested]: {
  label: "Requested",
  color: "info"
},
[FundingPackageStatus.Deficient]: {
  label: "Deficient",
  color: "warning"
},
[FundingPackageStatus.Funded]: {
  label: "Funded",
  color: "success"
},
[FundingPackageStatus.Cancelled]: {
  label: "Cancelled",
  color: "error"
}
};
